import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES, Reveal, Eyebrow, EASE } from "./shared";

const STEPS = [
  { n: "01", title: "Understand", text: "Every successful venture begins with understanding the market, its people and its opportunities. We invest time in learning the commercial landscape before making decisions because meaningful growth starts with informed thinking." },
  { n: "02", title: "Build", text: "We create businesses, partnerships and strategies that are purpose-built for the markets they serve. Strong foundations create enduring businesses." },
  { n: "03", title: "Execute", text: "Ideas only create value when they're executed consistently. Through disciplined operations and dependable partnerships, we focus on sustainable outcomes rather than short-term wins." },
  { n: "04", title: "Grow", text: "Growth isn't measured by speed. It's measured by resilience. Every venture is built with a long-term perspective, allowing it to evolve alongside the markets it serves." },
];

export const FortimWay = () => {
  const lineRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: lineRef, offset: ["start 75%", "start 30%"] });
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <>
      <section
        id="way"
        data-testid="way-section"
        className="relative py-28 lg:py-40"
        style={{ backgroundColor: "#F2F1EF" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <Reveal><Eyebrow>The Fortim Way</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-bold leading-[1.08] text-3xl sm:text-4xl lg:text-5xl" style={{ color: "#2B2B2B" }}>
                Understand. Build.<br />
                <span style={{ color: "#3B2C82" }}>Execute. Grow.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.12} as="p" className="mt-7 text-base lg:text-lg leading-relaxed" >
              <span style={{ color: "#8C8C8C" }}>
                Every venture begins with understanding. Every partnership is built on trust. Every
                decision is guided by long-term thinking.
              </span>
            </Reveal>
          </div>

          {/* Numbers row + connecting line */}
          <div ref={lineRef} className="relative mt-20">
            <div className="hidden lg:block absolute top-[18px] left-0 right-0 h-px" style={{ backgroundColor: "#D8D8D8" }} />
            <motion.div
              className="hidden lg:block absolute top-[18px] left-0 right-0 h-[2px] origin-left"
              style={{ backgroundColor: "#F5821F", scaleX }}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.n}
                  data-testid={`way-step-${s.title.toLowerCase()}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, ease: EASE, delay: i * 0.14 }}
                  className="relative"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="relative z-10 w-9 h-9 flex items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: "#3B2C82" }}>
                      {s.n}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-3" style={{ color: "#2B2B2B" }}>{s.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#8C8C8C" }}>{s.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Closing statement banner */}
      <section data-testid="way-banner" className="relative min-h-[70vh] flex items-center overflow-hidden">
        <img
          src={IMAGES.way}
          alt="Aerial view of a coastal solar farm at dusk"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(31,26,56,0.6)" }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 w-full py-24">
          <Reveal className="max-w-4xl">
            <span className="h-px w-12 block mb-8" style={{ background: "#F5821F" }} />
            <p className="text-white font-medium text-2xl sm:text-3xl lg:text-[2.6rem]" style={{ lineHeight: 1.5 }}>
              Our ambition isn’t simply to participate in the markets we enter. It’s to help shape
              their future by building businesses that create lasting value for partners,
              communities and the region as a whole.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
};
